import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Loader2, Save, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { TEMA_CLARO } from './panel/RespuestasEnFilas';
import { tonoDeRespuesta } from '../lib/respuestasDelCierre';
import { diaCorto, diaDelCierre, lineaDelDia, detalleDelDia, renglonesDelCierre } from '../lib/detalleDelCierre';
import { hoyLocal } from '../lib/horaEspana';

// Tonos de celda (panel en blanco) según calidad de la respuesta.
const TONE = {
    green: 'bg-emerald-500/15 text-emerald-800',
    amber: 'bg-amber-500/15 text-amber-800',
    red:   'bg-red-500/15 text-red-800',
    muted: 'text-[#57544E]/70 italic',
    none:  'text-[#57544E]',
};

const fmt = (iso) => {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) +
            ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
};

const percentTone = (v) => v == null ? 'muted' : (v >= 80 ? 'green' : v >= 50 ? 'amber' : 'red');
const sleepTone = (v) => v == null ? 'muted' : (v >= 7 ? 'green' : v >= 5 ? 'amber' : 'red');
const stressTone = (v) => v == null ? 'muted' : (v <= 4 ? 'green' : v <= 7 ? 'amber' : 'red');
const txt = (v) => (v == null || v === '' ? null : String(v));

const WEEKLY_COLS = [
    { key: 'created_at', label: 'Fecha', r: e => ({ v: fmt(e.created_at) }) },
    { key: 'weight', label: 'Peso', r: e => ({ v: e.weight != null ? `${e.weight} kg` : null }) },
    { key: 'tc', label: 'Cumpl. entreno', r: e => ({ v: e.training_compliance != null ? `${e.training_compliance}%` : null, t: percentTone(e.training_compliance) }) },
    { key: 'nc', label: 'Cumpl. nutrición', r: e => ({ v: e.nutrition_compliance != null ? `${e.nutrition_compliance}%` : null, t: percentTone(e.nutrition_compliance) }) },
    { key: 'sleep', label: 'Sueño', r: e => ({ v: e.sleep_quality != null ? `${e.sleep_quality}/10` : null, t: sleepTone(e.sleep_quality) }) },
    { key: 'stress', label: 'Estrés', r: e => ({ v: e.stress_level != null ? `${e.stress_level}/10` : null, t: stressTone(e.stress_level) }) },
    { key: 'notes', label: 'Notas', r: e => ({ v: txt(e.notes) }) },
];

const MONTHLY_COLS = [
    { key: 'created_at', label: 'Fecha', r: e => ({ v: fmt(e.created_at) }) },
    { key: 'weight', label: 'Peso', r: e => ({ v: e.weight != null ? `${e.weight} kg` : null }) },
    { key: 'bf', label: '% Grasa', r: e => ({ v: e.body_fat_pct != null ? `${e.body_fat_pct}%` : null }) },
    { key: 'meas', label: 'Medidas (P/C/Cad/Br/M)', r: e => {
        const m = e.measurements || {};
        return { v: ['chest', 'waist', 'hip', 'arm', 'thigh'].map(k => m[k] != null ? m[k] : '-').join(' / ') };
    } },
    { key: 'goals', label: 'Progreso objetivos', r: e => ({ v: txt(e.goals_progress), t: e.goals_progress ? 'green' : 'muted' }) },
    { key: 'chal', label: 'Retos', r: e => ({ v: txt(e.challenges), t: e.challenges ? 'amber' : 'muted' }) },
    // AQUÍ IBAN DOS COLUMNAS con la suplementación y el cumplimiento de dieta de los
    // reportes de Calma, y se han quitado a la espera de que Jesús decida (Francisco,
    // 14-08: «olvida ese campo nuevo, luego lo miramos, los datos mantenlos»).
    //
    // Los DATOS siguen: la migración escribía en «Comentario cliente» la cadena cruda
    // «Importado de Calma. suplementacion=... cumplimiento=...», y eso ya está partido en
    // `calma_suplementacion` y `calma_cumplimiento_dieta` en la base, con el comentario
    // limpio. O sea que esa columna vuelve a enseñar lo que escribió el cliente y nada
    // más; los dos datos están guardados y listos para cuando se decida dónde van.
    { key: 'notes', label: 'Comentario cliente', r: e => ({ v: txt(e.notes), t: e.notes ? 'amber' : 'muted' }) },
];

// EL DIARIO, COMO LOS MENSUALES (21-10): lista de días y el detalle al tocar uno. La línea
// y el detalle son los de «Tus días» del cliente (`lib/detalleDelCierre`): el entrenador lee
// el día con las mismas palabras que él, con lo que no se le preguntó fuera, «Entreno: sin
// registro» en gris para un `trained` false sin respuesta (P69, doc 23-08) y, en «Notas»,
// solo las que el cliente compartió (las «Solo para ti» ya no salen del servidor,
// `core/notas_del_cierre`).
//
// El color de las tres respuestas que pueden ser un fallo (`tonoDeRespuesta`): «No tocaba»,
// «No tenía» y «No los tengo pautados» no lo son y van en gris. El `trained` de los cierres
// viejos no se colorea: no es una respuesta a esta pregunta.
const TONO_DEL_DETALLE = {
    Entreno: c => tonoDeRespuesta('entreno', c.entreno_respuesta),
    Cardio: c => tonoDeRespuesta('cardio', c.cardio),
    Suplementos: c => tonoDeRespuesta('suplementos', c.suplementos?.respuesta),
};
const TONO_DE_LA_RESPUESTA = { fallo: 'red', bien: 'green' };

const detalleDelDiario = (c) => [
    ...detalleDelDia(c, hoyLocal()).map(f => ({
        key: f.nombre, label: f.nombre, v: f.valor,
        t: f.apagado ? 'muted' : (TONO_DE_LA_RESPUESTA[TONO_DEL_DETALLE[f.nombre]?.(c)] || 'none'),
    })),
    ...renglonesDelCierre(c).map(r => ({ key: `texto-${r.titulo}`, label: r.titulo, v: r.texto })),
];

const resumenDelDia = (c) => lineaDelDia(c).join(' · ') || 'sin respuestas';

// El día del cierre («Lun 24»), no la hora a la que lo mandó: un cierre de las 00:30 es del
// día anterior, y el `created_at` va en UTC.
const fechaDelDia = (c) => {
    const dia = diaDelCierre(c);
    return dia ? diaCorto(dia, hoyLocal()) : fmt(c.created_at);
};

// LOS 5 ÚLTIMOS A LA VISTA (Francisco, 21-10) y «Mostrar todas (N)» despliega los que haya
// (la petición trae hasta 200).
const A_LA_VISTA = 5;

// Quién escribió el feedback y cuándo (21-10, `core/firma_del_feedback`, como en el mensual).
// Los de antes no lo traen y no se pinta nada.
const firmaDelFeedback = (e) => {
    if (e?.feedback_de) return `Escrito por ${e.feedback_de}${e.feedback_at ? ` · ${fmt(e.feedback_at)}` : ''}`;
    return e?.feedback_at ? `Escrito el ${fmt(e.feedback_at)}` : '';
};

// Listado de fechas + detalle en modal. Con decenas de envíos, la tabla entera
// desplegada era ilegible; y si el cliente no ha enviado nada, el bloque no se pinta.
//
// `columns` dice qué se enseña de cada envío; `detalle` (el diario) lo da ya hecho, en filas
// {key, label, v, t}. `fecha` es lo que encabeza cada fila (por defecto, día y hora del envío).
const _filasDeLasColumnas = (columns) => (e) => columns.filter(c => c.key !== 'created_at')
    .map(c => ({ key: c.key, label: c.label, t: 'none', ...(c.r(e) || {}) }));

// LA NOTA PRIVADA DEL ENTRENADOR (Francisco, 22-10: «vuelve a poner la nota privada»). Solo en
// el check-in diario (`onNota`), el que mandan hoy los clientes, al lado del «Feedback para el
// cliente» y no en su lugar: el feedback lo lee el cliente y la nota no (va por su propia ruta,
// PUT /admin/clients/{id}/checkins/{id}/nota, y las rutas del cliente no la devuelven).
const CheckinsLista = ({ title, tituloDelDetalle, columns, detalle, rows, onFeedback, onNota, resumen, fecha = e => fmt(e.created_at) }) => {
    const [abiertoId, setAbiertoId] = useState(null);
    const [borrador, setBorrador] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [todas, setTodas] = useState(false);
    const [nota, setNota] = useState('');
    const [guardandoNota, setGuardandoNota] = useState(false);

    if (!rows.length) return null;

    const abierto = rows.find(r => r.id === abiertoId) || null;
    const filasDelAbierto = abierto ? (detalle || _filasDeLasColumnas(columns))(abierto) : [];
    const vistos = todas ? rows : rows.slice(0, A_LA_VISTA);
    const abrir = (e) => { setBorrador(e.trainer_feedback || ''); setNota(e.nota_del_entrenador || ''); setAbiertoId(e.id); };
    const guardar = async () => {
        setGuardando(true);
        try { await onFeedback(abierto.id, borrador); setAbiertoId(null); }
        finally { setGuardando(false); }
    };
    const guardarNota = async () => {
        setGuardandoNota(true);
        try { await onNota(abierto.id, nota); }
        finally { setGuardandoNota(false); }
    };

    return (
        <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
            <div className="px-5 pt-5 pb-3 border-b border-[#E5E2DB] flex items-baseline justify-between gap-3">
                <p className="text-xs font-bold text-[#57544E] uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#B43E06]" /> {title}
                </p>
                <span className="text-xs text-[#57544E]">{rows.length} {rows.length === 1 ? 'envío' : 'envíos'}</span>
            </div>
            <ul className="divide-y divide-[#E5E2DB] max-h-[22rem] overflow-y-auto">
                {vistos.map((e, i) => (
                    <li key={e.id || i}>
                        <button onClick={() => abrir(e)} data-testid="checkin-fila"
                            className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[#F6F5F2] transition-colors">
                            <span className="text-[#1C1B19] text-sm tabular-nums whitespace-nowrap">{fecha(e)}</span>
                            <span className="text-[#57544E] text-xs truncate">{resumen(e)}</span>
                            <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                                {onNota && e.nota_del_entrenador && (
                                    <span className="text-[#B43E06] text-[10px] uppercase tracking-wider">con nota</span>
                                )}
                                {e.trainer_feedback
                                    ? <span className="text-emerald-700 text-[10px] uppercase tracking-wider">con feedback</span>
                                    : <span className="text-[#57544E]/70 text-[10px] uppercase tracking-wider">sin feedback</span>}
                                <ChevronRight className="w-4 h-4 text-[#57544E]/70" />
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
            {rows.length > A_LA_VISTA && (
                <button type="button" onClick={() => setTodas(t => !t)} data-testid="checkins-mostrar-todas"
                    className="w-full py-2.5 border-t border-[#E5E2DB] text-sm font-semibold text-[#57544E] hover:text-[#1C1B19] hover:bg-[#F6F5F2] transition-colors">
                    {todas ? 'Mostrar menos' : `Mostrar todas (${rows.length})`}
                </button>
            )}

            <Dialog open={!!abierto} onOpenChange={(o) => !o && setAbiertoId(null)}>
                {abierto && (
                    <DialogContent style={TEMA_CLARO} className="bg-white [color-scheme:light] border-[#E5E2DB] max-w-3xl max-h-[90vh] overflow-y-auto text-[#1C1B19]" data-testid="checkin-detail">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-wider">{tituloDelDetalle || title.replace('Check-ins', 'Check-in').replace(/(al)es$|s$/, '$1')}</DialogTitle>
                        </DialogHeader>
                        <p className="text-[#57544E] text-xs -mt-2">{fecha(abierto)}</p>
                        <dl className="grid grid-cols-[9rem_1fr] sm:grid-cols-[12rem_1fr] gap-x-4 gap-y-2 text-sm">
                            {filasDelAbierto.map(c => {
                                const { v, t } = c;
                                return (
                                    <React.Fragment key={c.key}>
                                        <dt className="text-[#57544E] text-xs uppercase tracking-wider pt-0.5">{c.label}</dt>
                                        <dd className={v == null ? TONE.muted : (TONE[t] || TONE.none)}>
                                            <span className="whitespace-pre-wrap leading-relaxed">{v == null ? 'sin respuesta' : v}</span>
                                        </dd>
                                    </React.Fragment>
                                );
                            })}
                        </dl>
                        {!filasDelAbierto.length && <p className={`text-sm ${TONE.muted}`}>Sin respuestas</p>}
                        <div>
                            <p className="text-[#57544E] text-xs mb-1">Feedback para el cliente</p>
                            <textarea value={borrador} onChange={e => setBorrador(e.target.value)} rows={3}
                                placeholder="Escribe feedback para el cliente..."
                                className="w-full bg-[#F6F5F2] border border-[#E5E2DB] rounded-lg px-3 py-2 text-sm text-[#1C1B19] focus:outline-none focus:border-[#FF671F]" />
                            {abierto.trainer_feedback && firmaDelFeedback(abierto) && (
                                <p className="text-[11px] text-[#57544E] mt-1" data-testid="firma-del-feedback">
                                    {firmaDelFeedback(abierto)}
                                </p>
                            )}
                        </div>
                        {onNota && (
                            <div>
                                <label htmlFor={`nota-${abierto.id}`} className="block text-[#57544E] text-xs mb-1">
                                    Notas del entrenador <span className="text-[#6F6B64]">(el cliente no la ve)</span>
                                </label>
                                <textarea id={`nota-${abierto.id}`} value={nota} onChange={e => setNota(e.target.value)}
                                    rows={3} maxLength={4000} data-testid="nota-del-entrenador"
                                    placeholder="Escribe una nota sobre este día..."
                                    className="w-full bg-[#FEF9E7] border border-[#F1DFA0] rounded-lg px-3 py-2 text-sm text-[#1C1B19] focus:outline-none focus:border-[#FF671F]" />
                                <div className="flex justify-end mt-1">
                                    <button type="button" onClick={guardarNota} data-testid="guardar-nota-del-entrenador"
                                        disabled={guardandoNota || nota === (abierto.nota_del_entrenador || '')}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E2DB] text-[#1C1B19] disabled:opacity-40">
                                        {guardandoNota ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar nota
                                    </button>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <button onClick={() => setAbiertoId(null)}
                                className="px-4 py-2 rounded-lg text-sm border border-[#E5E2DB] text-[#1C1B19]">Cerrar</button>
                            <button onClick={guardar} disabled={guardando || borrador === (abierto.trainer_feedback || '')}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF671F] text-white disabled:opacity-40">
                                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar feedback
                            </button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
};

const CoachCheckins = ({ clientId }) => {
    const { api } = useAuth();
    const [checkins, setCheckins] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            // Sin el semáforo de salud («Saludable / Atención / En riesgo») ni las «Fotos de
            // progreso»: las dos se quitaron de la ficha el 21-10 (las fotos están en la
            // sección Fotos de Reportes mensuales).
            const ci = await api.get(`/admin/clients/${clientId}/checkins?limit=200`).catch(() => ({ data: [] }));
            setCheckins(Array.isArray(ci.data) ? ci.data : []);
        } finally {
            setLoading(false);
        }
    }, [api, clientId]);

    useEffect(() => { load(); }, [load]);

    const weekly = useMemo(() => checkins.filter(c => c.type === 'weekly'), [checkins]);
    const monthly = useMemo(() => checkins.filter(c => c.type === 'monthly'), [checkins]);
    const daily = useMemo(() => checkins.filter(c => c.type === 'daily'), [checkins]);

    const saveFeedback = async (checkinId, feedback) => {
        try {
            const { data } = await api.post(`/admin/clients/${clientId}/checkins/${checkinId}/feedback`, { feedback });
            setCheckins(cs => cs.map(c => c.id === checkinId ? {
                ...c, trainer_feedback: feedback,
                feedback_de: data?.feedback_de ?? null, feedback_at: data?.feedback_at ?? null,
            } : c));
            toast.success('Feedback guardado');
        } catch (e) {
            console.error('No se pudo guardar el feedback del check-in:', e);
            toast.error('No se pudo guardar el feedback. Inténtalo de nuevo.');
        }
    };

    const saveNota = async (checkinId, nota) => {
        try {
            const { data } = await api.put(`/admin/clients/${clientId}/checkins/${checkinId}/nota`, { nota });
            setCheckins(cs => cs.map(c => c.id === checkinId ? { ...c, nota_del_entrenador: data?.nota_del_entrenador ?? null } : c));
            toast.success('Nota guardada');
        } catch (e) {
            console.error('No se pudo guardar la nota del entrenador:', e);
            toast.error('No se pudo guardar la nota. Inténtalo de nuevo.');
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#57544E]/70" /></div>;
    }

    return (
        <div className="space-y-4">
            {checkins.length === 0 && (
                <p className="text-[#6F6B64] text-sm text-center py-10" data-testid="sin-checkins">
                    Todavía no hay check-ins.
                </p>
            )}
            <CheckinsLista title="Check-ins diarios" detalle={detalleDelDiario} rows={daily} onFeedback={saveFeedback} onNota={saveNota}
                resumen={resumenDelDia} fecha={fechaDelDia} />
            <CheckinsLista title="Check-ins semanales" columns={WEEKLY_COLS} rows={weekly} onFeedback={saveFeedback}
                resumen={e => [e.weight != null && `${e.weight} kg`,
                               e.training_compliance != null && `entreno ${e.training_compliance}%`,
                               e.nutrition_compliance != null && `nutrición ${e.nutrition_compliance}%`]
                               .filter(Boolean).join(' · ')} />
            {/* LOS «MENSUALES» SON LOS FORMULARIOS DE CALMA (Francisco, 22-10): 1.564 de los 1.566 se
                importaron al migrar (`_migrar_todos.py`, «Importado de Calma») y la app no crea
                ninguno. Van al final y con su nombre, para que no parezcan el reporte mensual,
                que está en «Reportes mensuales». */}
            <CheckinsLista title="Formularios de Calma (importados)" tituloDelDetalle="Formulario de Calma" columns={MONTHLY_COLS} rows={monthly} onFeedback={saveFeedback}
                resumen={e => [e.weight != null && `${e.weight} kg`,
                               e.body_fat_pct != null && `${e.body_fat_pct}% graso`]
                               .filter(Boolean).join(' · ')} />
        </div>
    );
};

export default CoachCheckins;
